import { useRef, useState, useEffect } from "react";
import Compressor from "compressorjs";
import { uploadImageWithProgress } from "../../../config/firebaseConfig";
import { useAuth } from "../../../context/AuthContext";

const EditorJawaban = ({ onChange, clean, value }) => {
  const { user } = useAuth();

  const editorRef = useRef(null);
  const prevImagesRef = useRef([]);
  const savedRangeRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isList, setIsList] = useState(false);
  const [isListNumber, setIsListNumber] = useState(false);

  /* ===============================
     RESET
  =============================== */
  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = "";

    prevImagesRef.current = [];
    savedRangeRef.current = null;

    setProgress(0);
    setIsLoading(false);

    setIsBold(false);
    setIsItalic(false);
    setIsUnderline(false);
    setIsList(false);
    setIsListNumber(false);

    onChange && onChange("");
    console.log(clean)
  }, [clean]);

  /* ===============================
     SET VALUE
  =============================== */
  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      editorRef.current.innerHTML = value;
      onChange(value)
    }
  }, [value]);

  /* ===============================
     SAVE & RESTORE CURSOR
  =============================== */
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRangeRef.current = sel.getRangeAt(0);
    }
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    if (savedRangeRef.current && sel) {
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
    }
  };

  /* ===============================
     UPDATE HTML
  =============================== */
  const handleInput = () => {
    onChange && onChange(editorRef.current.innerHTML);
  };

  /* ===============================
     TEXT FORMAT
  =============================== */
  const exec = (command) => {
    editorRef.current.focus();
    document.execCommand(command);
    handleInput();
  };

  const onBold = () => {
    exec("bold");
    setIsBold(document.queryCommandState("bold"));
  };

  const onItalic = () => {
    exec("italic");
    setIsItalic(document.queryCommandState("italic"));
  };

  const onUnderline = () => {
    exec("underline");
    setIsUnderline(document.queryCommandState("underline"));
  };

  const onList = () => {
    exec("insertUnorderedList");
    setIsList(document.queryCommandState("insertUnorderedList"));
  };

  const onOrderedList = () => {
    exec("insertOrderedList");
    setIsListNumber(document.queryCommandState("insertOrderedList"));
  };

  /* ===============================
     INSERT IMAGE (FIXED)
  =============================== */
  const insertImage = (url) => {
    restoreSelection();
    editorRef.current.focus();

    const img = document.createElement("img");
    img.src = url;
    img.style.maxWidth = "100%";
    img.style.margin = "10px 0";

    const range = window.getSelection().getRangeAt(0);
    range.insertNode(img);

    range.setStartAfter(img);
    range.setEndAfter(img);

    handleInput();
  };

  /* ===============================
     UPLOAD IMAGE
  =============================== */
  const uploadFile = (file) => {
    if (!user?.uid) return;

    saveSelection();
    setIsLoading(true);

    uploadImageWithProgress(
      file,
      user.uid,
      "editor",

      (prog) => setProgress(prog),

      (url) => {
        insertImage(url);
        prevImagesRef.current.push(url);
        setIsLoading(false);
        setProgress(0);
      },

      (err) => {
        console.error(err);
        setIsLoading(false);
      }
    );
  };

  const handleUpload = (file) => {
    if (file.size > 5 * 1024 * 1024) {
      new Compressor(file, {
        quality: 0.6,
        maxWidth: 1920,
        maxHeight: 1080,
        success: uploadFile,
      });
    } else {
      uploadFile(file);
    }
  };

  const handlePilihFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";

    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) handleUpload(file);
    };

    input.click();
  };

  /* ===============================
     PASTE HANDLER
  =============================== */
  const handlePaste = (e) => {
    e.preventDefault();
    saveSelection();

    const items = e.clipboardData.items;
    const text = e.clipboardData.getData("text/plain");

    for (let item of items) {
      if (item.type.includes("image")) {
        handleUpload(item.getAsFile());
        return;
      }
    }

    document.execCommand("insertText", false, text);
  };

  /* ===============================
     UI
  =============================== */
  return (
    <div className="col-md-12">

      {/* TOOLBAR */}
      <div className="d-flex flex-wrap gap-2 mb-4">
        <button onClick={onBold} className={`btn btn-outline-dark ${isBold ? "active" : ""}`}><b>B</b></button>
        <button onClick={onItalic} className={`btn btn-outline-dark ${isItalic ? "active" : ""}`}><i>I</i></button>
        <button onClick={onUnderline} className={`btn btn-outline-dark ${isUnderline ? "active" : ""}`}><u>U</u></button>
        <button onClick={onList} className={`btn btn-outline-dark ${isList ? "active" : ""}`}>• List</button>
        <button onClick={onOrderedList} className={`btn btn-outline-dark ${isListNumber ? "active" : ""}`}>1. List</button>
        <button onClick={handlePilihFile} className="btn btn-outline-dark">📷</button>
      </div>

      {/* PROGRESS */}
      {isLoading && (
        <div className="progress mb-4">
          <div className="progress-bar" style={{ width: `${progress}%` }}>
            {progress.toFixed(0)}%
          </div>
        </div>
      )}

      {/* EDITOR */}
      <div
        ref={editorRef}
        className="editor form-control"
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        suppressContentEditableWarning
        style={{ minHeight: "150px", padding: "10px" }}
      />
    </div>
  );
};

export default EditorJawaban;